import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Card, Button, Form, Alert, Spinner, Badge,
  Table, Modal, InputGroup, ProgressBar, ListGroup, Toast, ToastContainer,
  Offcanvas
} from 'react-bootstrap';
import { 
  CreditCard, AlertCircle, TrendingUp, DollarSign, Calendar,
  Eye, Check, X, Clock, User, Phone, MapPin, RefreshCcw,
  Filter, Menu, ArrowLeft
} from 'lucide-react';
import saleService from '../../services/saleService';
import { userService } from '../../services/userService';

/**
 * Composant de gestion des crédits clients
 * Interface responsive pour le suivi des crédits en cours
 */
const CreditManagement = ({ onBack, onViewCustomer, onError }) => {
  // État principal
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Statistiques
  const [stats, setStats] = useState({
    totalCredits: 0,
    totalAmount: 0,
    overdueCount: 0,
    overdueAmount: 0
  });

  // Filtres
  const [filters, setFilters] = useState({
    status: 'all', // all, pending, overdue, recent
    period: 'all', // all, week, month, older
    customer: ''
  });

  // Modal de détails
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState(null);

  // Modal de paiement
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [processingPayment, setProcessingPayment] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState([]);

  // Responsive
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Mobile filters
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Effect pour responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Chargement initial
  useEffect(() => {
    loadCredits();
  }, []);

  /**
   * Afficher une notification
   */
  const showNotification = (type, title, message) => {
    const notification = {
      id: Date.now(),
      type,
      title,
      message,
      timestamp: new Date()
    };
    
    setNotifications(prev => [...prev, notification]);
    
    // Supprimer automatiquement après 5 secondes
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  /**
   * Charger les ventes à crédit
   */
  const loadCredits = async () => {
    try {
      setLoading(true);
      setError(null);

      // Essayer d'abord avec l'endpoint spécialisé pour les crédits
      let salesResponse;
      try {
        salesResponse = await userService.getCreditSales();
      } catch (error) {
        // Si l'endpoint spécialisé n'existe pas, utiliser l'endpoint général
        console.log('📋 Endpoint crédit spécialisé non disponible, utilisation de l\'endpoint général...');
        salesResponse = await saleService.getSales();
      }
      
      if (salesResponse.success) {
        // Filtrer les ventes à crédit non soldées
        const creditSales = salesResponse.data
          .filter(sale => sale.isCredit && !sale.isCreditPaid)
          .map(sale => enhanceCreditData(sale));
        
        setCredits(creditSales);
        calculateCreditStats(creditSales);
        console.log('✅ Crédits chargés:', creditSales.length);
        
        if (creditSales.length === 0) {
          showNotification('info', 'Information', 'Aucun crédit en cours trouvé');
        }
      } else {
        setError(salesResponse.message || 'Erreur lors du chargement');
        showNotification('error', 'Erreur', salesResponse.message || 'Erreur lors du chargement des crédits');
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des crédits:', error);
      setError('Erreur de connexion');
      showNotification('error', 'Erreur', 'Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Enrichir les données de crédit
   */
  const enhanceCreditData = (sale) => {
  const now = new Date();
  const saleDate = new Date(sale.createdAt);
  const daysDiff = Math.floor((now - saleDate) / (1000 * 60 * 60 * 24));
  
  // Déterminer le statut du crédit
  let status = 'recent';
  let urgencyLevel = 'low';
  
  if (daysDiff > 30) {
    status = 'overdue';
    urgencyLevel = 'high';
  } else if (daysDiff > 7) {
    status = 'pending';
    urgencyLevel = 'medium';
  }
  
  // ✅ CORRECTION : Utiliser les valeurs du backend si disponibles
  // creditPaid et creditBalance sont déjà calculés correctement par le backend
  const creditPaid = parseFloat(sale.creditPaid || sale.credit_paid || 0);
  const total = parseFloat(sale.total || 0);
  
  // ✅ Utiliser creditBalance du backend si disponible, sinon calculer
  const remainingAmount = sale.creditBalance !== undefined 
    ? parseFloat(sale.creditBalance)
    : (sale.credit_balance !== undefined 
        ? parseFloat(sale.credit_balance)
        : (total - creditPaid));
  
  console.log('🔍 Enhancement crédit:', {
    saleNumber: sale.saleNumber,
    total,
    creditPaid,
    creditBalanceBackend: sale.creditBalance || sale.credit_balance,
    remainingAmountCalculated: remainingAmount
  });
  
  return {
    ...sale,
    daysSinceCreation: daysDiff,
    status,
    urgencyLevel,
    remainingAmount, // ✅ Valeur correcte du backend
    creditPaid, // ✅ Assurer que c'est un nombre
    formattedDate: saleDate.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }),
    formattedAmount: formatAmount(remainingAmount)
  };
};


  /**
   * Calculer les statistiques des crédits
   */
  const calculateCreditStats = (creditsList) => {
    const stats = {
      totalCredits: creditsList.length,
      totalAmount: creditsList.reduce((sum, credit) => sum + credit.remainingAmount, 0),
      overdueCount: creditsList.filter(c => c.status === 'overdue').length,
      overdueAmount: creditsList
        .filter(c => c.status === 'overdue')
        .reduce((sum, credit) => sum + credit.remainingAmount, 0)
    };
    setStats(stats);
  };

  /**
   * Filtrer les crédits
   */
  const getFilteredCredits = () => {
    let filtered = [...credits];

    // Filtre par statut
    if (filters.status !== 'all') {
      filtered = filtered.filter(credit => credit.status === filters.status);
    }

    // Filtre par période
    if (filters.period !== 'all') {
      const now = new Date();
      filtered = filtered.filter(credit => {
        const saleDate = new Date(credit.createdAt);
        const daysDiff = Math.floor((now - saleDate) / (1000 * 60 * 60 * 24));
        
        switch (filters.period) {
          case 'week':
            return daysDiff <= 7;
          case 'month':
            return daysDiff <= 30;
          case 'older':
            return daysDiff > 30;
          default:
            return true;
        }
      });
    }

    // Filtre par nom de client
    if (filters.customer) {
      const searchTerm = filters.customer.toLowerCase();
      filtered = filtered.filter(credit =>
        credit.customer?.name?.toLowerCase().includes(searchTerm)
      );
    }

    // Trier par urgence et date
    return filtered.sort((a, b) => {
      const urgencyOrder = { high: 3, medium: 2, low: 1 };
      const urgencyDiff = urgencyOrder[b.urgencyLevel] - urgencyOrder[a.urgencyLevel];
      
      if (urgencyDiff !== 0) return urgencyDiff;
      
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  };

  /**
   * Formater un montant
   */
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  /**
   * Afficher les détails d'un crédit
   */
  const handleViewCredit = (credit) => {
    setSelectedCredit(credit);
    setShowDetailModal(true);
  };

  /**
   * Ouvrir le modal de paiement
   */
  const handlePayCredit = (credit) => {
    setSelectedCredit(credit);
    setPaymentAmount(credit.remainingAmount.toString());
    setShowPaymentModal(true);
  };

  /**
   * Traiter le paiement d'un crédit
   */
  
const handleProcessPayment = async () => {
  if (!selectedCredit || !paymentAmount || parseFloat(paymentAmount) <= 0) {
    setError('Montant de paiement invalide');
    showNotification('error', 'Erreur', 'Montant de paiement invalide');
    return;
  }

  const amount = parseFloat(paymentAmount);
  if (amount > selectedCredit.remainingAmount) {
    setError('Le montant ne peut pas dépasser le solde restant');
    showNotification('error', 'Erreur', 'Le montant ne peut pas dépasser le solde restant');
    return;
  }

  try {
    setProcessingPayment(true);
    setError(null);
    
    console.log('💳 Traitement paiement:', {
      saleId: selectedCredit.id,
      amount,
      paymentMethod,
      remainingAmountBefore: selectedCredit.remainingAmount
    });
    
    // Appeler la fonction de paiement
    const response = await saleService.payCreditSale(selectedCredit.id, {
      amount,
      paymentMethod
    });

    if (response.success) {
      console.log('✅ Paiement traité, données retournées:', response.data);
      
      // ✅ Fermer le modal
      setShowPaymentModal(false);
      setPaymentAmount('');
      
      // ✅ NOUVEAU : Mettre à jour directement la liste avec les nouvelles données
      const updatedSale = response.data;
      
      // Calculer le nouveau montant restant
      const newCreditPaid = parseFloat(updatedSale.creditPaid || updatedSale.credit_paid || 0);
      const total = parseFloat(updatedSale.total || 0);
      const newRemainingAmount = total - newCreditPaid;
      const isNowPaid = newCreditPaid >= total;
      
      console.log('📊 Nouvelles valeurs:', {
        creditPaid: newCreditPaid,
        total,
        remainingAmount: newRemainingAmount,
        isCreditPaid: isNowPaid
      });
      
      if (isNowPaid) {
        // ✅ Si le crédit est maintenant soldé, le retirer de la liste
        console.log('💚 Crédit soldé, retrait de la liste');
        setCredits(prevCredits => 
          prevCredits.filter(credit => credit.id !== selectedCredit.id)
        );
        
        // Recalculer les stats
        const newCreditsList = credits.filter(credit => credit.id !== selectedCredit.id);
        calculateCreditStats(newCreditsList);
        
        showNotification('success', 'Crédit soldé', 
          `Le crédit de ${selectedCredit.customer.name} a été entièrement payé !`);
      } else {
        // ✅ Si c'est un paiement partiel, mettre à jour les valeurs du crédit
        console.log('💛 Paiement partiel, mise à jour de la liste');
        setCredits(prevCredits => 
          prevCredits.map(credit => {
            if (credit.id === selectedCredit.id) {
              // Créer un crédit mis à jour avec les nouvelles valeurs
              const updatedCredit = {
                ...credit,
                creditPaid: newCreditPaid,
                credit_paid: newCreditPaid,
                creditBalance: newRemainingAmount,
                credit_balance: newRemainingAmount,
                remainingAmount: newRemainingAmount,
                isCreditPaid: isNowPaid,
                is_credit_paid: isNowPaid,
                formattedAmount: formatAmount(newRemainingAmount)
              };
              
              console.log('🔄 Crédit mis à jour:', updatedCredit);
              return updatedCredit;
            }
            return credit;
          })
        );
        
        // Recalculer les stats avec la nouvelle liste
        const updatedCreditsList = credits.map(credit => {
          if (credit.id === selectedCredit.id) {
            return {
              ...credit,
              remainingAmount: newRemainingAmount
            };
          }
          return credit;
        });
        calculateCreditStats(updatedCreditsList);
        
        showNotification('success', 'Paiement enregistré', 
          `Paiement de ${formatAmount(amount)} traité avec succès. Reste à payer: ${formatAmount(newRemainingAmount)}`);
      }
      
      setSelectedCredit(null);
      console.log('✅ Liste des crédits mise à jour');
      
    } else {
      setError(response.message || 'Erreur lors du paiement');
      showNotification('error', 'Erreur de paiement', response.message || 'Erreur lors du paiement');
    }
  } catch (error) {
    console.error('❌ Erreur lors du paiement:', error);
    setError('Erreur lors du traitement du paiement');
    showNotification('error', 'Erreur', 'Erreur lors du traitement du paiement');
  } finally {
    setProcessingPayment(false);
  }
};


  /**
   * Actualiser les données
   */
  const handleRefresh = () => {
    loadCredits();
  };

  // Données filtrées
  const filteredCredits = getFilteredCredits();

  // Rendu du loading
  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2">Chargement des crédits...</div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="credit-management py-3">
      {/* Header responsive */}
      <div className={`d-flex align-items-center mb-4 ${isMobile ? 'justify-content-between' : ''}`}>
        <div className="d-flex align-items-center">
          {onBack && isMobile && (
            <Button variant="link" onClick={onBack} className="p-1 me-2">
              <ArrowLeft size={20} />
            </Button>
          )}
          <h2 className="mb-0">
            <CreditCard className="me-2" size={24} />
            Gestion des Crédits
          </h2>
        </div>
        <div className="d-flex gap-2">
          {isMobile && (
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={() => setShowMobileFilters(true)}
            >
              <Filter size={16} />
            </Button>
          )}
          {onBack && !isMobile && (
            <Button variant="outline-secondary" onClick={onBack}>
              <ArrowLeft size={16} className="me-1" />
              Retour
            </Button>
          )}
          <Button 
            variant="outline-primary" 
            onClick={handleRefresh}
            disabled={loading}
            size={isMobile ? "sm" : ""}
          >
            <RefreshCcw size={16} className={!isMobile ? "me-1" : ""} />
            {!isMobile && "Actualiser"}
          </Button>
        </div>
      </div>

      {/* Statistiques principales */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <Card className="stat-card h-100">
            <Card.Body className={`text-center ${isMobile ? 'py-3' : ''}`}>
              <CreditCard size={isMobile ? 20 : 24} className="text-primary mb-2" />
              <h4 className={`mb-0 ${isMobile ? 'h6' : ''}`}>{stats.totalCredits}</h4>
              <small className="text-muted">Crédits en cours</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stat-card h-100">
            <Card.Body className={`text-center ${isMobile ? 'py-3' : ''}`}>
              <DollarSign size={isMobile ? 20 : 24} className="text-success mb-2" />
              <div className={`mb-0 fw-bold ${isMobile ? 'small' : 'h6'}`}>
                {formatAmount(stats.totalAmount)}
              </div>
              <small className="text-muted">Montant total</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stat-card h-100">
            <Card.Body className={`text-center ${isMobile ? 'py-3' : ''}`}>
              <AlertCircle size={isMobile ? 20 : 24} className="text-danger mb-2" />
              <h4 className={`mb-0 ${isMobile ? 'h6' : ''}`}>{stats.overdueCount}</h4>
              <small className="text-muted">En retard</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stat-card h-100">
            <Card.Body className={`text-center ${isMobile ? 'py-3' : ''}`}>
              <TrendingUp size={isMobile ? 20 : 24} className="text-warning mb-2" />
              <div className={`mb-0 fw-bold ${isMobile ? 'small' : 'h6'}`}>
                {formatAmount(stats.overdueAmount)}
              </div>
              <small className="text-muted">Montant en retard</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filtres - Desktop uniquement */}
      {!isMobile && (
        <Card className="mb-4">
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col md={4}>
                <Form.Label>Rechercher un client</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <User size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Nom du client..."
                    value={filters.customer}
                    onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Label>Statut</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="all">Tous</option>
                  <option value="recent">Récents</option>
                  <option value="pending">En attente</option>
                  <option value="overdue">En retard</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Période</Form.Label>
                <Form.Select
                  value={filters.period}
                  onChange={(e) => setFilters({ ...filters, period: e.target.value })}
                >
                  <option value="all">Toutes</option>
                  <option value="week">Cette semaine</option>
                  <option value="month">Ce mois</option>
                  <option value="older">Plus ancien</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Button variant="outline-secondary" onClick={() => setFilters({
                  status: 'all',
                  period: 'all',
                  customer: ''
                })}>
                  Réinitialiser
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Message d'erreur */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <AlertCircle size={16} className="me-2" />
          {error}
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-2"
            onClick={() => setError(null)}
          >
            <X size={14} />
          </Button>
        </Alert>
      )}

      {/* Liste des crédits */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            Liste des Crédits ({filteredCredits.length})
          </h5>
        </Card.Header>
        <Card.Body className={isMobile ? "p-2" : "p-0"}>
          {filteredCredits.length === 0 ? (
            <div className="text-center py-5">
              <CreditCard size={48} className="text-muted mb-3" />
              <h6>Aucun crédit trouvé</h6>
              <p className="text-muted">
                {filters.status !== 'all' || filters.customer
                  ? 'Essayez de modifier vos filtres'
                  : 'Aucun crédit en cours actuellement'}
              </p>
            </div>
          ) : (
            <>
              {/* Version Desktop - Table */}
              {!isMobile && (
                <Table responsive hover>
                  <thead className="bg-light">
                    <tr>
                      <th>Client</th>
                      <th>Date</th>
                      <th>Montant restant</th>
                      <th>Statut</th>
                      <th>Urgence</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCredits.map((credit) => (
                      <tr key={credit.id}>
                        <td>
                          <div>
                            <strong>{credit.customer?.name || 'Client inconnu'}</strong>
                            {credit.customer?.phone && (
                              <div className="small text-muted">
                                <Phone size={12} className="me-1" />
                                {credit.customer.phone}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div>{credit.formattedDate}</div>
                          <small className="text-muted">
                            Il y a {credit.daysSinceCreation} jour{credit.daysSinceCreation > 1 ? 's' : ''}
                          </small>
                        </td>
                        <td>
                          <strong className="text-primary">
                            {credit.formattedAmount}
                          </strong>
                        </td>
                        <td>
                          <Badge bg={getStatusBadgeVariant(credit.status)}>
                            {getStatusText(credit.status)}
                          </Badge>
                        </td>
                        <td>
                          <Badge 
                            bg={credit.urgencyLevel === 'high' ? 'danger' : 
                                credit.urgencyLevel === 'medium' ? 'warning' : 'success'}
                            text={credit.urgencyLevel === 'warning' ? 'dark' : 'white'}
                          >
                            {credit.urgencyLevel === 'high' ? 'Élevée' : 
                             credit.urgencyLevel === 'medium' ? 'Moyenne' : 'Faible'}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewCredit(credit)}
                              title="Voir les détails"
                            >
                              <Eye size={14} />
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handlePayCredit(credit)}
                              title="Enregistrer un paiement"
                            >
                              <DollarSign size={14} />
                            </Button>
                            {onViewCustomer && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => onViewCustomer(credit.customer?.id)}
                                title="Voir le client"
                              >
                                <User size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              {/* Version Mobile - Cards */}
              {isMobile && (
                <div className="d-grid gap-3">
                  {filteredCredits.map((credit) => (
                    <Card key={credit.id} className="shadow-sm">
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <strong className="d-block">
                              {credit.customer?.name || 'Client inconnu'}
                            </strong>
                            {credit.customer?.phone && (
                              <small className="text-muted">
                                <Phone size={12} className="me-1" />
                                {credit.customer.phone}
                              </small>
                            )}
                          </div>
                          <Badge bg={getStatusBadgeVariant(credit.status)}>
                            {getStatusText(credit.status)}
                          </Badge>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div className="text-primary fw-bold fs-5">
                            {credit.formattedAmount}
                          </div>
                          <small className="text-muted">
                            {credit.formattedDate}
                          </small>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <Badge 
                            bg={credit.urgencyLevel === 'high' ? 'danger' : 
                                credit.urgencyLevel === 'medium' ? 'warning' : 'success'}
                            text={credit.urgencyLevel === 'warning' ? 'dark' : 'white'}
                          >
                            Urgence {credit.urgencyLevel === 'high' ? 'élevée' : 
                                     credit.urgencyLevel === 'medium' ? 'moyenne' : 'faible'}
                          </Badge>
                          
                          <small className="text-muted">
                            <Clock size={12} className="me-1" />
                            Il y a {credit.daysSinceCreation} jour{credit.daysSinceCreation > 1 ? 's' : ''}
                          </small>
                        </div>

                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewCredit(credit)}
                            className="flex-fill"
                          >
                            <Eye size={14} className="me-1" />
                            Détails
                          </Button>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handlePayCredit(credit)}
                            className="flex-fill"
                          >
                            <DollarSign size={14} className="me-1" />
                            Payer
                          </Button>
                          {onViewCustomer && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => onViewCustomer(credit.customer?.id)}
                            >
                              <User size={14} />
                            </Button>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Offcanvas Filtres Mobile */}
      <Offcanvas
        show={showMobileFilters}
        onHide={() => setShowMobileFilters(false)}
        placement="start"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <Filter size={20} className="me-2" />
            Filtres
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Rechercher un client</Form.Label>
              <Form.Control
                type="text"
                placeholder="Nom du client..."
                value={filters.customer}
                onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Statut</Form.Label>
              <Form.Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="all">Tous les statuts</option>
                <option value="recent">Récents</option>
                <option value="pending">En attente</option>
                <option value="overdue">En retard</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Période</Form.Label>
              <Form.Select
                value={filters.period}
                onChange={(e) => setFilters({ ...filters, period: e.target.value })}
              >
                <option value="all">Toutes les périodes</option>
                <option value="week">Cette semaine</option>
                <option value="month">Ce mois</option>
                <option value="older">Plus ancien</option>
              </Form.Select>
            </Form.Group>

            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                onClick={() => setShowMobileFilters(false)}
              >
                Appliquer les filtres
              </Button>
              <Button 
                variant="outline-secondary" 
                onClick={() => {
                  setFilters({
                    status: 'all',
                    period: 'all',
                    customer: ''
                  });
                  setShowMobileFilters(false);
                }}
              >
                Réinitialiser
              </Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Modal détails du crédit */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Eye className="me-2" size={20} />
            Détails du Crédit
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCredit && (
            <Row>
              <Col md={6}>
                <Card className="h-100">
                  <Card.Header>
                    <h6 className="mb-0">
                      <User className="me-2" size={16} />
                      Informations Client
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <p><strong>Nom:</strong> {selectedCredit.customer?.name || 'Non renseigné'}</p>
                    {selectedCredit.customer?.phone && (
                      <p>
                        <Phone size={14} className="me-2" />
                        {selectedCredit.customer.phone}
                      </p>
                    )}
                    {selectedCredit.customer?.address && (
                      <p>
                        <MapPin size={14} className="me-2" />
                        {selectedCredit.customer.address}
                      </p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="h-100">
                  <Card.Header>
                    <h6 className="mb-0">
                      <CreditCard className="me-2" size={16} />
                      Détails du Crédit
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <p><strong>Date de vente:</strong> {selectedCredit.formattedDate}</p>
                    <p><strong>Montant total:</strong> {formatAmount(selectedCredit.total)}</p>
                    <p><strong>Montant payé:</strong> {formatAmount(selectedCredit.creditPaid || 0)}</p>
                    <p><strong>Montant restant:</strong> 
                      <span className="text-primary fw-bold ms-1">
                        {selectedCredit.formattedAmount}
                      </span>
                    </p>
                    <p><strong>Statut:</strong> 
                      <Badge bg={getStatusBadgeVariant(selectedCredit.status)} className="ms-1">
                        {getStatusText(selectedCredit.status)}
                      </Badge>
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Fermer
          </Button>
          {selectedCredit && selectedCredit.remainingAmount > 0 && (
            <Button
              variant="success"
              onClick={() => {
                handlePayCredit(selectedCredit);
                setShowDetailModal(false);
              }}
            >
              <DollarSign size={16} className="me-1" />
              Enregistrer un paiement
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Modal paiement */}
      <Modal
        show={showPaymentModal}
        onHide={() => {
          if (!processingPayment) {
            setShowPaymentModal(false);
            setPaymentAmount('');
          }
        }}
        centered
      >
        <Modal.Header closeButton={!processingPayment}>
          <Modal.Title>
            <DollarSign className="me-2" size={20} />
            Enregistrer un Paiement
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCredit && (
            <>
              <div className="mb-3 p-3 bg-light rounded">
                <p className="mb-1"><strong>Client:</strong> {selectedCredit.customer?.name}</p>
                <p className="mb-0"><strong>Montant restant:</strong> 
                  <span className="text-primary fw-bold ms-1">
                    {selectedCredit.formattedAmount}
                  </span>
                </p>
              </div>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Montant du paiement</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>XOF</InputGroup.Text>
                    <Form.Control
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      placeholder="Montant"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      max={selectedCredit.remainingAmount}
                      min="0"
                      step="0.01"
                      disabled={processingPayment}
                    />
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Montant maximum: {selectedCredit.formattedAmount}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Mode de paiement</Form.Label>
                  <Form.Select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    disabled={processingPayment}
                  >
                    <option value="CASH">💵 Espèces</option>
                    <option value="WAVE">📱Wave</option>
                    <option value="ORANGE_MONEY">🍊 Orange Money</option>
                    <option value="CARD">💳 Carte bancaire</option>
                    <option value="BANK_TRANSFER">🏦 Virement bancaire</option>
                    <option value="CHECK">📋 Chèque</option>
                  </Form.Select>
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowPaymentModal(false);
              setPaymentAmount('');
            }}
            disabled={processingPayment}
          >
            Annuler
          </Button>
          <Button
            variant="success"
            onClick={handleProcessPayment}
            disabled={
              !paymentAmount || 
              parseFloat(paymentAmount) <= 0 || 
              parseFloat(paymentAmount) > (selectedCredit?.remainingAmount || 0) ||
              processingPayment
            }
          >
            {processingPayment ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Traitement...
              </>
            ) : (
              <>
                <Check size={16} className="me-1" />
                Confirmer le paiement
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Notifications Toast */}
      <ToastContainer position="top-end" className="p-3">
        {notifications.map((notification) => (
          <Toast
            key={notification.id}
            onClose={() => setNotifications(prev => 
              prev.filter(n => n.id !== notification.id)
            )}
            show={true}
            delay={5000}
            autohide
          >
            <Toast.Header>
              <strong className="me-auto">
                {notification.type === 'success' ? (
                  <Check size={16} className="text-success me-1" />
                ) : notification.type === 'error' ? (
                  <AlertCircle size={16} className="text-danger me-1" />
                ) : (
                  <AlertCircle size={16} className="text-info me-1" />
                )}
                {notification.title}
              </strong>
              <small className="text-muted">À l'instant</small>
            </Toast.Header>
            <Toast.Body>{notification.message}</Toast.Body>
          </Toast>
        ))}
      </ToastContainer>

      <style jsx>{`
        .credit-management .stat-card {
          transition: all 0.2s ease;
        }
        
        .credit-management .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }

        @media (max-width: 767px) {
          .credit-management .table {
            font-size: 0.875rem;
          }
        }
      `}</style>
    </Container>
  );

  // Fonctions utilitaires
  function getStatusBadgeVariant(status) {
    switch (status) {
      case 'overdue':
        return 'danger';
      case 'pending':
        return 'warning';
      case 'recent':
        return 'success';
      default:
        return 'secondary';
    }
  }

  function getStatusText(status) {
    switch (status) {
      case 'overdue':
        return 'En retard';
      case 'pending':
        return 'En attente';
      case 'recent':
        return 'Récent';
      default:
        return 'Inconnu';
    }
  }
};

export default CreditManagement;